import type {
  Alarm,
  Batch,
  BatchTracking,
  ModuleSnapshot,
  SafetyState,
  SystemMode,
  TrayLoad,
  TrayUnload,
} from '@ctcells/contract'

import { orchestrator } from '../clients.ts'
import { keys } from '../keys.ts'
import { normalizeList, type Listed } from '../page.ts'
import { createQuery, queryParams, type SkippableArgs } from '../query.ts'

/**
 * 운영 도메인 조회 — 배치 · 모듈 · 안전 · 운영모드 · 알람 · 적재/수거.
 *
 * 🔴 전부 **스냅샷 1회**다. 갱신은 구독 → 무효화로 온다(DP-01).
 * ⚠ 정렬 파라미터가 **어느 경로에도 없다**(실측). 목록 화면의 열 정렬은 현재 페이지 안에서만 된다.
 */

export interface PageArgs extends SkippableArgs {
  /** 1~500, 기본 50. 범위를 벗어나면 서버가 422 를 준다 */
  limit?: number
  offset?: number
}

export interface BatchListArgs extends PageArgs {
  status?: Batch['status']
  recipe_id?: string
  /**
   * 🔴 **불리언이 아니다.** 실서버가 `const "pending"` 한 값만 받는다(실측 2026-09-20) —
   *    `true` 를 보내면 422 다. 「검토 대기만 걸러 보기」라는 뜻이고 반대값이 없다.
   */
  review?: 'pending'
  /** `active` = 진행 중 · `finished` = 종착. 상시 작업 줄이 쓰는 축 */
  phase?: 'active' | 'finished'
}

export const useBatches = createQuery<BatchListArgs, Listed<Batch>>({
  key: (args) => keys.batches.list(args),
  fetch: async (args) =>
    normalizeList<Batch>(
      await orchestrator.request({ method: 'GET', path: '/batches', query: queryParams(args) }),
      args,
    ),
})

export const useBatch = createQuery<{ batchId: string | undefined }, Batch>({
  key: ({ batchId }) => keys.batches.detail(batchId ?? ''),
  fetch: ({ batchId }) =>
    orchestrator.request<Batch>({ method: 'GET', path: `/batches/${String(batchId)}` }),
  enabled: ({ batchId }) => batchId !== undefined && batchId !== '',
})

/**
 * **배치 추적**(`GET /batches/{id}/tracking` · IFC-RO-111 · 서버 v0.9.4) — 그 배치 트레이의 지금 위치 + 챔버(웰)별 시료 상태.
 *
 * 🔴 **스냅샷 1회 + 구독이다**(DP-01). 이후 갱신은 구독 `batch_tracking` 프레임이 이 키(`keys.tracking.detail(id)`)를
 *    다시 받게 한다(`stream/bridge.ts`). 서버는 위치·상태를 바꾸는 사건이 커밋될 때만, 그것도 내용이 바뀌었을 때만 낸다 — 주기가 아니다.
 * ⚠ 모든 역할이 읽는다(서버 `views/batch_tracking.py` `require_roles(ROLES)` · 계약 `x-roles: [admin, operator, viewer]`) —
 *   `patient_id` 를 싣지 않아서다. 없는 배치는 404 `NOT_FOUND`.
 * ⚠ 서버 v0.9.4 에서 자원 점유(`GET /resource-locks`)가 걷혔다 — 「이 배치가 6축 이송 중인가」는 이 응답의 `trays[].where` 가 답한다
 *   (`ui/screens/monitoring/chamberProgress.ts` `batchTransferring`).
 */
export const useBatchTracking = createQuery<
  { batchId: string | undefined } & SkippableArgs,
  BatchTracking
>({
  key: ({ batchId }) => keys.tracking.detail(batchId ?? ''),
  fetch: ({ batchId }) =>
    orchestrator.request<BatchTracking>({
      method: 'GET',
      path: `/batches/${String(batchId)}/tracking`,
    }),
  enabled: ({ batchId }) => batchId !== undefined && batchId !== '',
})

export interface ModuleListArgs extends PageArgs {
  module_type?: ModuleSnapshot['module_type']
  control_owner?: ModuleSnapshot['control_owner']
}

/**
 * 🔴 `ModuleSnapshot.allowed_commands` 가 버튼 활성의 권위다. 화면은 `commandGate()` 로만 읽는다.
 * ⚠ 실물은 모듈 4건 전부 **빈 배열**을 준다(2026-09-17 실측) — 규약대로면 제어 버튼 전부 비활성이 정상이다.
 */
export const useModules = createQuery<ModuleListArgs, Listed<ModuleSnapshot>>({
  // skip 은 관찰자의 실행 조건이다. 같은 HTTP 목록을 {} / { skip: false } 두 캐시로 나누지 않는다.
  key: (args) => keys.modules.list(queryParams(args)),
  fetch: async (args) =>
    normalizeList<ModuleSnapshot>(
      await orchestrator.request({ method: 'GET', path: '/modules', query: queryParams(args) }),
      args,
    ),
})

/**
 * ⚠ **인자 없이도 부를 수 있다**(`useSafetyState()`). `SkippableArgs | void` 라서 그렇다 —
 *   질의 파라미터가 없는 경로라 넘길 것은 `skip` 뿐이고, 그 하나 때문에 모든 호출부가
 *   빈 객체를 쓰게 만들지 않는다.
 * 🔴 `skip` 이 필요한 이유는 하나다 — 데이터 소스가 `demo` 인 도메인은 **실물을 때리지 않는다**
 *    (「실물을 때렸는데 목이 답했다」 방지). 「더 자주 부르기」 축은 여전히 열려 있지 않다.
 */
export const useSafetyState = createQuery<SkippableArgs | void, SafetyState>({
  key: () => keys.safety.state(),
  fetch: () => orchestrator.request<SafetyState>({ method: 'GET', path: '/safety' }),
})

export const useSystemMode = createQuery<SkippableArgs | void, SystemMode>({
  key: () => keys.systemMode.current(),
  fetch: () => orchestrator.request<SystemMode>({ method: 'GET', path: '/system/mode' }),
})

export interface AlarmListArgs extends PageArgs {
  state?: Alarm['state']
  severity?: NonNullable<Alarm['severity']>
  source?: string
}

/**
 * 알람 스냅샷. 🔴 **서버가 받는 축은 `state` · `severity` · `source` 셋뿐이다**(실측 · 기간 없음).
 *
 * ⚠ **`category` 를 기대하지 마라.** 응답에 없고 **구독 프레임에도 없다** — 그 필드는
 *   `EventPayload`(MQTT · L1 구독 금지)에만 있고 저장 표에 열조차 없다. 화면 탭은 `state`
 *   축으로 짜여 있다(`AlarmsScreen` 주석 · BR-08).
 */
export const useAlarms = createQuery<AlarmListArgs, Listed<Alarm>>({
  key: (args) => keys.alarms.list(args),
  fetch: async (args) =>
    normalizeList<Alarm>(
      await orchestrator.request({ method: 'GET', path: '/alarms', query: queryParams(args) }),
      args,
    ),
})

export interface TrayListArgs extends SkippableArgs {
  /** 확약 대기만 — `confirmed_at === null` 인 것 */
  pending?: boolean
  batch_id?: string
  module_id?: string
}

/** ⚠ 실물이 **벌거벗은 배열**을 준다(`limit`·`offset` 파라미터도 없다). 정규화가 흡수한다 */
export const useLoads = createQuery<TrayListArgs, Listed<TrayLoad>>({
  key: (args) => keys.loads.list(args),
  fetch: async (args) =>
    normalizeList<TrayLoad>(
      await orchestrator.request({ method: 'GET', path: '/loads', query: queryParams(args) }),
    ),
})

export const useUnloads = createQuery<TrayListArgs, Listed<TrayUnload>>({
  key: (args) => keys.unloads.list(args),
  fetch: async (args) =>
    normalizeList<TrayUnload>(
      await orchestrator.request({ method: 'GET', path: '/unloads', query: queryParams(args) }),
    ),
})
